import { useState } from "react";
import { Save, X } from "lucide-react";
import { ImageUpload } from "./ImageUpload";

const LANG_TABS = [["it", "Italiano"], ["es", "Español"], ["en", "English"]];
const EMPTY = {
  slug: "", image: "", date: new Date().toISOString().slice(0, 10), published: false,
  category: { es: "", en: "", it: "" }, title: { es: "", en: "", it: "" }, excerpt: { es: "", en: "", it: "" },
  content: { es: [], en: [], it: [] },
};

const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

const inputCls = "w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-ink placeholder:text-muted/60 outline-none focus:border-pine focus:ring-2 focus:ring-pine/15";
const labelCls = "mb-1.5 block text-xs font-mono uppercase tracking-widest text-sage";

export const PostEditor = ({ initial, onSave, onCancel, saving, token }) => {
  const [post, setPost] = useState(initial || EMPTY);
  const [lang, setLang] = useState("it");
  const isNew = !initial;

  const setField = (k) => (e) => setPost({ ...post, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const setLoc = (k) => (e) => setPost({ ...post, [k]: { ...post[k], [lang]: e.target.value } });
  const setContent = (e) => setPost({ ...post, content: { ...post.content, [lang]: e.target.value.split(/\n\s*\n/) } });
  const onTitle = (e) => {
    const next = { ...post, title: { ...post.title, [lang]: e.target.value } };
    if (isNew && lang === "it") next.slug = slugify(e.target.value);
    setPost(next);
  };

  const submit = (e) => {
    e.preventDefault();
    onSave(post);
  };

  const filled = (l) => post.title[l] && post.content[l]?.some((p) => p.trim());

  return (
    <form onSubmit={submit} className="rounded-3xl border border-border bg-white p-6 sm:p-8 space-y-5" data-testid="post-editor">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-serif text-2xl text-ink">{isNew ? "Nuovo articolo" : "Modifica articolo"}</h3>
        <button type="button" onClick={onCancel} data-testid="post-editor-cancel" className="inline-flex items-center gap-1 text-sm text-muted hover:text-ink"><X size={15} /> Annulla</button>
      </div>

      <div className="grid gap-4 sm:grid-cols-[1fr_160px]">
        <div>
          <label htmlFor="pe-slug" className={labelCls}>Slug (URL) *</label>
          <input id="pe-slug" data-testid="post-editor-slug" required value={post.slug} onChange={setField("slug")} className={`${inputCls} font-mono`} placeholder="il-mio-articolo" />
        </div>
        <div>
          <label htmlFor="pe-date" className={labelCls}>Data *</label>
          <input id="pe-date" data-testid="post-editor-date" type="date" required value={post.date} onChange={setField("date")} className={`${inputCls} font-mono`} />
        </div>
      </div>
      <div>
        <span className={labelCls}>Immagine di copertina</span>
        <ImageUpload value={post.image} onChange={(image) => setPost({ ...post, image })} token={token} />
      </div>

      <div className="flex gap-2 pt-2" data-testid="post-editor-lang-tabs">
        {LANG_TABS.map(([code, label]) => (
          <button
            key={code}
            type="button"
            onClick={() => setLang(code)}
            data-testid={`post-editor-lang-${code}`}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${lang === code ? "bg-pine text-linen" : "border border-border text-ink/70 hover:border-pine/50"}`}
          >
            {label}{filled(code) ? " ✓" : ""}
          </button>
        ))}
      </div>

      <div>
        <label htmlFor="pe-title" className={labelCls}>Titolo ({lang}) *</label>
        <input id="pe-title" data-testid="post-editor-title" required={lang === "it"} value={post.title[lang]} onChange={onTitle} className={`${inputCls} font-serif text-lg`} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="pe-cat" className={labelCls}>Categoria ({lang})</label>
          <input id="pe-cat" data-testid="post-editor-category" value={post.category[lang]} onChange={setLoc("category")} className={inputCls} placeholder="Salute intestinale" />
        </div>
      </div>
      <div>
        <label htmlFor="pe-exc" className={labelCls}>Estratto ({lang})</label>
        <textarea id="pe-exc" data-testid="post-editor-excerpt" rows={2} value={post.excerpt[lang]} onChange={setLoc("excerpt")} className={`${inputCls} resize-none`} placeholder="Due righe che riassumono l'articolo" />
      </div>
      <div>
        <label htmlFor="pe-content" className={labelCls}>Testo ({lang}) — separa i paragrafi con una riga vuota</label>
        <textarea id="pe-content" data-testid="post-editor-content" rows={14} value={post.content[lang].join("\n\n")} onChange={setContent} className={`${inputCls} leading-relaxed`} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
        <label className="flex items-center gap-2.5 cursor-pointer text-sm text-ink">
          <input type="checkbox" data-testid="post-editor-published" checked={post.published} onChange={setField("published")} className="h-4 w-4 accent-pine" />
          Pubblicato (visibile sul sito)
        </label>
        <button type="submit" disabled={saving} data-testid="post-editor-save" className="inline-flex items-center gap-2 rounded-full bg-clay px-7 py-3 text-sm font-semibold text-white hover:bg-clay-dark disabled:opacity-50">
          <Save size={16} /> {saving ? "Salvataggio..." : "Salva articolo"}
        </button>
      </div>
    </form>
  );
};
